// Entering a company: from the picker after sign-in, and Switch company from
// inside the app. Neither signs out — sign-out ends the login on every device,
// and the person is only changing which of their companies this tab works in.

import { reset_store, sign_In } from '../redux/actions/action';
import { completeSignIn } from '../utils/completeSignIn';
import { claimsFromSessionToken } from '../utils/tokenClaims';
import { roleFromPayload } from '../utils/userRole';
import {
  isValidCompanyId,
  setCompanyId,
  clearCompanyChoice,
  rememberLastCompany,
  clearSessionForCompanySwitch
} from './companySession';
import { cachedMyCompanies, storeMyCompanies } from './myCompaniesCache';

const sessionValue = (key) => {
  try {
    return sessionStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

/**
 * Point this tab at a company and finish signing in there: the profile, role,
 * ERP type and caches all come from that company's record.
 */
export async function enterCompany(
  companyId,
  { dispatch, navigate, redirectTo, onProfileUnavailable }
) {
  if (!isValidCompanyId(companyId)) return false;
  const cognitoUsername = sessionValue('cognitoUsername');
  setCompanyId(companyId);
  clearCompanyChoice();
  rememberLastCompany(cognitoUsername, companyId);
  return completeSignIn({
    dispatch,
    navigate,
    token: sessionValue('token'),
    accessToken: sessionValue('accessToken'),
    cognitoUsername,
    tokenRole: roleFromPayload(claimsFromSessionToken()),
    redirectTo,
    onProfileUnavailable
  });
}

/**
 * Switch company without signing out:
 *   1. wipe the store (RESET_STORE);
 *   2. clear the session keys except the login (token, accessToken, cognitoUsername);
 *   3. set the company and load its profile;
 *   4. drop the socket, so it re-registers under the new record's id;
 *   5. reload into the destination.
 *
 * The Cognito sign-in payload is put back after the wipe: it is the login, not
 * the company, and is the same in every company.
 */
export async function switchCompany(
  companyId,
  { store, persistor, socket, destination = '/home', onProfileUnavailable } = {}
) {
  if (!isValidCompanyId(companyId) || !store) return false;
  const signInPayload = store.getState()?.signIn?.signIn;
  // The person's companies do not change with the company they are in.
  const myCompanies = cachedMyCompanies();

  store.dispatch(reset_store());
  clearSessionForCompanySwitch();
  if (signInPayload) store.dispatch(sign_In(signInPayload));
  if (myCompanies) storeMyCompanies(myCompanies);

  let route = destination;
  const entered = await enterCompany(companyId, {
    dispatch: store.dispatch,
    // The reload below is the navigation; remember only where sign-in sends a
    // role that has its own home (Admin, Vendor).
    navigate: (to) => {
      if (to && to !== '/home') route = to;
    },
    onProfileUnavailable
  });
  if (!entered) return false;

  try {
    socket?.disconnect?.();
  } catch (e) {
    // A socket that will not close is replaced by the reload anyway.
  }
  if (persistor?.flush) await persistor.flush();
  window.location.assign(route);
  return true;
}
